package er.prateek.crossly.ui.theme

import androidx.compose.material3.ColorScheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.ui.graphics.Color

enum class PaletteId {
    CLASSIC,
    OCEAN,
    SUNSET,
    NEON,
}

class AppColorPalette(
    val id: PaletteId,
    val light: GameColors,
    val dark: GameColors,
) {
    fun colors(dark: Boolean): GameColors = if (dark) this.dark else light
}

// Reward and difficulty colors are deliberately palette-independent: gold always
// means "star", flame always means "streak", and a player who recolors the app
// should still recognise an EXPERT tier at a glance.
private val GoldLight = Color(0xFFF5A524)
private val GoldDark = Color(0xFFFFC53D)
private val FlameLight = Color(0xFFFF6B35)
private val FlameDark = Color(0xFFFF8A4C)
private val SuccessLight = Color(0xFF16A34A)
private val SuccessDark = Color(0xFF34D399)
private val DangerLight = Color(0xFFE11D48)
private val DangerDark = Color(0xFFFB7185)

private val TierEasyLight = Color(0xFF16A34A)
private val TierMediumLight = Color(0xFF2563EB)
private val TierHardLight = Color(0xFFD97706)
private val TierExpertLight = Color(0xFFDC2626)
private val TierEasyDark = Color(0xFF4ADE80)
private val TierMediumDark = Color(0xFF60A5FA)
private val TierHardDark = Color(0xFFFBBF24)
private val TierExpertDark = Color(0xFFF87171)

// The active cell is gold in every palette — the crossword convention players
// already know, and it never collides with the accent-tinted word highlight.
private val FocusLight = Color(0xFFFFD24A)
private val FocusDark = Color(0xFF8A6A17)

/**
 * Builds a full [GameColors] from the handful of colors that actually differ
 * between palettes; everything else (rewards, tiers, cell status tints) is
 * derived so a new palette is ~12 lines instead of ~40.
 */
private fun lightColors(
    accent: Color,
    accentAlt: Color,
    accentSoft: Color,
    backdropTop: Color,
    backdropBottom: Color,
    glowA: Color,
    glowB: Color,
    cardBorder: Color,
    shadow: Color,
    ink: Color,
    inkMuted: Color,
    cellBorder: Color,
    cellBlock: Color,
    cellWord: Color,
): GameColors = GameColors(
    backdropTop = backdropTop,
    backdropBottom = backdropBottom,
    glowA = glowA,
    glowB = glowB,
    gridTexture = ink.copy(alpha = 0.05f),
    card = Color.White,
    cardElevated = Color.White,
    cardBorder = cardBorder,
    shadow = shadow,
    accent = accent,
    accentAlt = accentAlt,
    onAccent = Color.White,
    accentSoft = accentSoft,
    ink = ink,
    inkMuted = inkMuted,
    gold = GoldLight,
    goldEmpty = inkMuted.copy(alpha = 0.28f),
    flame = FlameLight,
    success = SuccessLight,
    danger = DangerLight,
    locked = inkMuted,
    cellFill = Color.White,
    cellBorder = cellBorder,
    cellText = ink,
    cellNumber = inkMuted,
    cellBlock = cellBlock,
    cellWord = cellWord,
    cellFocus = FocusLight,
    cellCorrect = Color(0xFFC5F0D3),
    cellIncorrect = Color(0xFFFFD3DC),
    cellRevealed = Color(0xFFE3DAFF),
    // Derived from the palette's own cell border so starters read as part of the
    // printed grid in every theme, rather than as a status the player caused.
    cellGiven = cellBorder.mixWith(Color.White, 0.35f),
    tierEasy = TierEasyLight,
    tierMedium = TierMediumLight,
    tierHard = TierHardLight,
    tierExpert = TierExpertLight,
)

private fun darkColors(
    accent: Color,
    accentAlt: Color,
    accentSoft: Color,
    backdropTop: Color,
    backdropBottom: Color,
    glowA: Color,
    glowB: Color,
    card: Color,
    cardElevated: Color,
    cardBorder: Color,
    ink: Color,
    inkMuted: Color,
    cellFill: Color,
    cellBorder: Color,
    cellBlock: Color,
    cellWord: Color,
): GameColors = GameColors(
    backdropTop = backdropTop,
    backdropBottom = backdropBottom,
    glowA = glowA,
    glowB = glowB,
    gridTexture = Color.White.copy(alpha = 0.04f),
    card = card,
    cardElevated = cardElevated,
    cardBorder = cardBorder,
    shadow = Color(0xFF04040C),
    accent = accent,
    accentAlt = accentAlt,
    onAccent = Color(0xFF0A0B16),
    accentSoft = accentSoft,
    ink = ink,
    inkMuted = inkMuted,
    gold = GoldDark,
    goldEmpty = inkMuted.copy(alpha = 0.3f),
    flame = FlameDark,
    success = SuccessDark,
    danger = DangerDark,
    locked = inkMuted,
    cellFill = cellFill,
    cellBorder = cellBorder,
    cellText = ink,
    cellNumber = inkMuted,
    cellBlock = cellBlock,
    cellWord = cellWord,
    cellFocus = FocusDark,
    cellCorrect = Color(0xFF1F5138),
    cellIncorrect = Color(0xFF5B2233),
    cellRevealed = Color(0xFF3D2E63),
    cellGiven = cellFill.mixWith(cellBorder, 0.55f),
    tierEasy = TierEasyDark,
    tierMedium = TierMediumDark,
    tierHard = TierHardDark,
    tierExpert = TierExpertDark,
)

private val Classic = AppColorPalette(
    id = PaletteId.CLASSIC,
    light = lightColors(
        accent = Color(0xFF5B5BD6),
        accentAlt = Color(0xFF8B5CF6),
        accentSoft = Color(0xFFE7E9FF),
        backdropTop = Color(0xFFF6F6FF),
        backdropBottom = Color(0xFFE7EAFF),
        glowA = Color(0xFFC3C9FF),
        glowB = Color(0xFFFFD4E8),
        cardBorder = Color(0xFFE1E5F8),
        shadow = Color(0xFFC3C9EA),
        ink = Color(0xFF1B1E38),
        inkMuted = Color(0xFF767CA3),
        cellBorder = Color(0xFFD6DBF0),
        cellBlock = Color(0xFF2A2E4A),
        cellWord = Color(0xFFDCE1FF),
    ),
    dark = darkColors(
        accent = Color(0xFF8B8BFF),
        accentAlt = Color(0xFFC08BFF),
        accentSoft = Color(0xFF2B2E5C),
        backdropTop = Color(0xFF15162E),
        backdropBottom = Color(0xFF0A0B18),
        glowA = Color(0xFF3B2E8F),
        glowB = Color(0xFF7A2E6B),
        card = Color(0xFF1B1D38),
        cardElevated = Color(0xFF23264A),
        cardBorder = Color(0xFF2F3358),
        ink = Color(0xFFEDEFFF),
        inkMuted = Color(0xFF9096C4),
        cellFill = Color(0xFF232645),
        cellBorder = Color(0xFF353A68),
        cellBlock = Color(0xFF0B0C1A),
        cellWord = Color(0xFF343A73),
    ),
)

private val Ocean = AppColorPalette(
    id = PaletteId.OCEAN,
    light = lightColors(
        accent = Color(0xFF0E9494),
        accentAlt = Color(0xFF1FA5D6),
        accentSoft = Color(0xFFD7F4F3),
        backdropTop = Color(0xFFF2FCFB),
        backdropBottom = Color(0xFFDDF2F4),
        glowA = Color(0xFFA9EDE8),
        glowB = Color(0xFFBCE0FF),
        cardBorder = Color(0xFFD3EAE9),
        shadow = Color(0xFFB0D3D3),
        ink = Color(0xFF10312F),
        inkMuted = Color(0xFF5F817F),
        cellBorder = Color(0xFFCCE5E4),
        cellBlock = Color(0xFF163B3A),
        cellWord = Color(0xFFC8EFEE),
    ),
    dark = darkColors(
        accent = Color(0xFF2DD4BF),
        accentAlt = Color(0xFF38BDF8),
        accentSoft = Color(0xFF15413F),
        backdropTop = Color(0xFF0A2124),
        backdropBottom = Color(0xFF04100F),
        glowA = Color(0xFF0C5B58),
        glowB = Color(0xFF14456E),
        card = Color(0xFF0F2A2C),
        cardElevated = Color(0xFF163639),
        cardBorder = Color(0xFF1E4447),
        ink = Color(0xFFE3F7F5),
        inkMuted = Color(0xFF80AAA8),
        cellFill = Color(0xFF15373A),
        cellBorder = Color(0xFF22504F),
        cellBlock = Color(0xFF04100F),
        cellWord = Color(0xFF1E5450),
    ),
)

private val Sunset = AppColorPalette(
    id = PaletteId.SUNSET,
    light = lightColors(
        accent = Color(0xFFE0542A),
        accentAlt = Color(0xFFF43F7E),
        accentSoft = Color(0xFFFFE3D8),
        backdropTop = Color(0xFFFFF7F2),
        backdropBottom = Color(0xFFFFE7DC),
        glowA = Color(0xFFFFCDB4),
        glowB = Color(0xFFFFC3DC),
        cardBorder = Color(0xFFF6DFD4),
        shadow = Color(0xFFE3BCA9),
        ink = Color(0xFF3A1B10),
        inkMuted = Color(0xFF96695A),
        cellBorder = Color(0xFFF2DCD1),
        cellBlock = Color(0xFF4A251A),
        cellWord = Color(0xFFFFDCCB),
    ),
    dark = darkColors(
        accent = Color(0xFFFF8A5B),
        accentAlt = Color(0xFFFF5C8A),
        accentSoft = Color(0xFF4A2318),
        backdropTop = Color(0xFF261209),
        backdropBottom = Color(0xFF140805),
        glowA = Color(0xFF7A2E14),
        glowB = Color(0xFF6E1B3C),
        card = Color(0xFF2E1710),
        cardElevated = Color(0xFF3A1E15),
        cardBorder = Color(0xFF4A281C),
        ink = Color(0xFFFFEDE4),
        inkMuted = Color(0xFFC29383),
        cellFill = Color(0xFF381D14),
        cellBorder = Color(0xFF522E20),
        cellBlock = Color(0xFF140805),
        cellWord = Color(0xFF56291B),
    ),
)

private val Neon = AppColorPalette(
    id = PaletteId.NEON,
    light = lightColors(
        accent = Color(0xFF7C3AED),
        accentAlt = Color(0xFFDB2777),
        accentSoft = Color(0xFFF0E4FF),
        backdropTop = Color(0xFFFBF6FF),
        backdropBottom = Color(0xFFF0E6FF),
        glowA = Color(0xFFD9BBFF),
        glowB = Color(0xFFB9F0FF),
        cardBorder = Color(0xFFE9DAFB),
        shadow = Color(0xFFC6ADE4),
        ink = Color(0xFF23103F),
        inkMuted = Color(0xFF7B6396),
        cellBorder = Color(0xFFE3D3F7),
        cellBlock = Color(0xFF32175A),
        cellWord = Color(0xFFEBDBFF),
    ),
    dark = darkColors(
        accent = Color(0xFFB06BFF),
        accentAlt = Color(0xFF22D3EE),
        accentSoft = Color(0xFF34195E),
        backdropTop = Color(0xFF190B2E),
        backdropBottom = Color(0xFF0B0413),
        glowA = Color(0xFF5B21B6),
        glowB = Color(0xFF0E7490),
        card = Color(0xFF211038),
        cardElevated = Color(0xFF2C1749),
        cardBorder = Color(0xFF3B2160),
        ink = Color(0xFFF3E9FF),
        inkMuted = Color(0xFF9E86C4),
        cellFill = Color(0xFF2A1547),
        cellBorder = Color(0xFF3E2368),
        cellBlock = Color(0xFF0B0413),
        cellWord = Color(0xFF3E2075),
    ),
)

val AppColorPalettes: List<AppColorPalette> = listOf(Classic, Ocean, Sunset, Neon)

fun paletteFor(id: PaletteId): AppColorPalette =
    AppColorPalettes.firstOrNull { it.id == id } ?: Classic

/**
 * Projects [GameColors] onto Material's scheme so stock components (Switch,
 * AlertDialog, TextField, ripples) stay in sync with the game palette instead
 * of drifting back to Material defaults.
 */
fun GameColors.toColorScheme(isDark: Boolean): ColorScheme {
    val base = if (isDark) darkColorScheme() else lightColorScheme()
    return base.copy(
        primary = accent,
        onPrimary = onAccent,
        primaryContainer = accentSoft,
        onPrimaryContainer = if (isDark) ink else accent,
        secondary = accentAlt,
        onSecondary = onAccent,
        secondaryContainer = accentSoft,
        onSecondaryContainer = if (isDark) ink else accentAlt,
        tertiary = gold,
        onTertiary = if (isDark) Color(0xFF201700) else Color.White,
        background = backdropBottom,
        onBackground = ink,
        surface = card,
        onSurface = ink,
        surfaceVariant = cardElevated,
        onSurfaceVariant = inkMuted,
        surfaceContainer = card,
        surfaceContainerHigh = cardElevated,
        surfaceContainerHighest = cardElevated,
        outline = cardBorder,
        outlineVariant = cardBorder,
        error = danger,
        onError = Color.White,
        errorContainer = cellIncorrect,
        onErrorContainer = if (isDark) ink else danger,
        scrim = Color(0xCC05060F),
    )
}
